import React, { useEffect } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import {
  Bold, Italic, Strikethrough, List, ListOrdered, Quote,
  Heading1, Heading2, Link as LinkIcon, Undo2, Redo2, Code,
} from 'lucide-react';

function ToolbarButton({ active, disabled, onClick, title, children }) {
  return (
    <button
      type="button"
      onMouseDown={(e) => e.preventDefault()}
      onClick={onClick}
      disabled={disabled}
      title={title}
      className={`p-1.5 rounded transition-colors ${
        active
          ? 'bg-[#7FD02E]/15 text-[#5BA51E]'
          : 'text-gray-600 hover:bg-gray-100'
      } disabled:opacity-40 disabled:cursor-not-allowed`}
    >
      {children}
    </button>
  );
}

/**
 * Rich text editor for ticket descriptions, comments and similar fields.
 * Stores HTML in the value prop.
 */
export default function RichTextEditor({
  value = '',
  onChange,
  placeholder = 'Escribe aquí…',
  minHeight = 140,
  readOnly = false,
  'data-testid': testId,
}) {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [1, 2] },
      }),
      Link.configure({
        openOnClick: false,
        autolink: true,
        HTMLAttributes: { class: 'text-[#5BA51E] underline' },
      }),
    ],
    content: value || '',
    editable: !readOnly,
    editorProps: {
      attributes: {
        class:
          'prose prose-sm max-w-none focus:outline-none px-3 py-2 ' +
          'prose-headings:my-2 prose-p:my-1 prose-ul:my-1 prose-ol:my-1 prose-li:my-0.5 ' +
          'prose-strong:text-gray-900',
        style: `min-height: ${minHeight}px;`,
        'data-placeholder': placeholder,
        ...(testId ? { 'data-testid': testId } : {}),
      },
    },
    onUpdate: ({ editor }) => {
      const html = editor.getHTML();
      // emit empty string when editor is empty
      onChange?.(editor.isEmpty ? '' : html);
    },
  });

  // Sync external value changes (e.g. form reset)
  useEffect(() => {
    if (!editor) return;
    const current = editor.getHTML();
    if ((value || '') !== current) {
      editor.commands.setContent(value || '', false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, editor]);

  if (!editor) return null;

  const setLink = () => {
    const previousUrl = editor.getAttributes('link').href;
    const url = window.prompt('URL', previousUrl || 'https://');
    if (url === null) return;
    if (url === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
      return;
    }
    editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
  };

  return (
    <div className="border border-gray-300 rounded-md focus-within:border-[#7FD02E] focus-within:ring-1 focus-within:ring-[#7FD02E] overflow-hidden bg-white">
      {!readOnly && (
        <div className="flex flex-wrap items-center gap-0.5 border-b border-gray-200 bg-gray-50 px-2 py-1">
          <ToolbarButton
            active={editor.isActive('bold')}
            onClick={() => editor.chain().focus().toggleBold().run()}
            title="Negrita (Ctrl+B)"
          >
            <Bold className="w-4 h-4" />
          </ToolbarButton>
          <ToolbarButton
            active={editor.isActive('italic')}
            onClick={() => editor.chain().focus().toggleItalic().run()}
            title="Cursiva (Ctrl+I)"
          >
            <Italic className="w-4 h-4" />
          </ToolbarButton>
          <ToolbarButton
            active={editor.isActive('strike')}
            onClick={() => editor.chain().focus().toggleStrike().run()}
            title="Tachado"
          >
            <Strikethrough className="w-4 h-4" />
          </ToolbarButton>
          <ToolbarButton
            active={editor.isActive('code')}
            onClick={() => editor.chain().focus().toggleCode().run()}
            title="Código en línea"
          >
            <Code className="w-4 h-4" />
          </ToolbarButton>
          <div className="w-px h-5 bg-gray-200 mx-1" />
          <ToolbarButton
            active={editor.isActive('heading', { level: 1 })}
            onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
            title="Título grande"
          >
            <Heading1 className="w-4 h-4" />
          </ToolbarButton>
          <ToolbarButton
            active={editor.isActive('heading', { level: 2 })}
            onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
            title="Subtítulo"
          >
            <Heading2 className="w-4 h-4" />
          </ToolbarButton>
          <div className="w-px h-5 bg-gray-200 mx-1" />
          <ToolbarButton
            active={editor.isActive('bulletList')}
            onClick={() => editor.chain().focus().toggleBulletList().run()}
            title="Lista con viñetas"
          >
            <List className="w-4 h-4" />
          </ToolbarButton>
          <ToolbarButton
            active={editor.isActive('orderedList')}
            onClick={() => editor.chain().focus().toggleOrderedList().run()}
            title="Lista numerada"
          >
            <ListOrdered className="w-4 h-4" />
          </ToolbarButton>
          <ToolbarButton
            active={editor.isActive('blockquote')}
            onClick={() => editor.chain().focus().toggleBlockquote().run()}
            title="Cita"
          >
            <Quote className="w-4 h-4" />
          </ToolbarButton>
          <div className="w-px h-5 bg-gray-200 mx-1" />
          <ToolbarButton
            active={editor.isActive('link')}
            onClick={setLink}
            title="Insertar enlace"
          >
            <LinkIcon className="w-4 h-4" />
          </ToolbarButton>
          <div className="ml-auto flex items-center gap-0.5">
            <ToolbarButton
              disabled={!editor.can().undo()}
              onClick={() => editor.chain().focus().undo().run()}
              title="Deshacer"
            >
              <Undo2 className="w-4 h-4" />
            </ToolbarButton>
            <ToolbarButton
              disabled={!editor.can().redo()}
              onClick={() => editor.chain().focus().redo().run()}
              title="Rehacer"
            >
              <Redo2 className="w-4 h-4" />
            </ToolbarButton>
          </div>
        </div>
      )}
      <EditorContent editor={editor} />
    </div>
  );
}

/**
 * Read-only renderer for HTML content stored by RichTextEditor.
 * Safely falls back when value is empty or plain text.
 */
export function RichTextView({ html, className = '', emptyText = 'Sin descripción' }) {
  if (!html || !html.trim()) {
    return <p className={`text-gray-400 italic ${className}`}>{emptyText}</p>;
  }
  // If value looks like plain text (no html tags), keep whitespace.
  const isPlain = !/<\/?[a-z][\s\S]*>/i.test(html);
  if (isPlain) {
    return <p className={`text-gray-700 whitespace-pre-wrap ${className}`}>{html}</p>;
  }
  return (
    <div
      className={`prose prose-sm max-w-none text-gray-700 ${className}`}
      // eslint-disable-next-line react/no-danger
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
}
